// src/components/community/Discussions.js
import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import useThreadStore from '../../hooks/useThreadStore';
import ThreadList from './ThreadList';
import ThreadDetail from './ThreadDetail';
import CreateThread from './CreateThread';
import './Discussions.css';

const Discussions = () => {
  const [view, setView] = useState('list'); // 'list', 'detail', 'create'
  const [selectedThreadId, setSelectedThreadId] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  
  // Track if component is mounted to prevent updates after unmount
  const isMounted = useRef(true);
  
  const {
    threads,
    thread,
    comments,
    loading,
    error,
    category,
    hasMore,
    fetchThread,
    changeCategory,
    loadMore,
    reset
  } = useThreadStore();

  // Set up the mounted ref
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      // Only call reset when component is unmounting
      if (reset) reset();
    };
  }, [reset]);

  // Categories for threads
  const categories = [
    { id: 'all', name: 'All Discussions' },
    { id: 'general', name: 'General' },
    { id: 'anime', name: 'Anime' },
    { id: 'manga', name: 'Manga' },
    { id: 'recommendations', name: 'Recommendations' },
    { id: 'news', name: 'News & Events' },
    { id: 'other', name: 'Other' }
  ];

  // Handle thread selection
  const viewThread = (threadObj) => {
    if (!isMounted.current) return;
    
    setSelectedThreadId(threadObj.id);
    setView('detail');
    // Update URL without page refresh
    navigate(`/community?tab=discussions&thread=${threadObj.id}`, { replace: true });
    
    // Fetch the thread details
    fetchThread(threadObj.id);
  };

  // Handle creating a new thread
  const createNewThread = () => {
    if (!isMounted.current) return;
    
    setView('create');
    navigate('/community?tab=discussions&action=create', { replace: true });
  };

  // Handle back to list
  const backToList = () => {
    if (!isMounted.current) return;
    
    setView('list');
    setSelectedThreadId(null);
    navigate('/community?tab=discussions', { replace: true });
  };

  // Handle thread creation success
  const handleThreadCreated = (newThread) => {
    if (!isMounted.current) return;
    
    setSelectedThreadId(newThread.id);
    setView('detail');
    navigate(`/community?tab=discussions&thread=${newThread.id}`, { replace: true });
    
    // Fetch the thread details
    fetchThread(newThread.id);
  };

  // Check URL params whenever location changes
  useEffect(() => {
    const checkUrlParams = () => {
      if (!isMounted.current) return;
      
      const urlParams = new URLSearchParams(location.search);
      const threadId = urlParams.get('thread');
      const action = urlParams.get('action');
      
      if (threadId) {
        setSelectedThreadId(threadId);
        setView('detail');
        fetchThread(threadId);
      } else if (action === 'create') {
        setView('create');
      } else {
        // If no thread ID or action in URL, show the list view
        setView('list');
        setSelectedThreadId(null);
      }
    };
    
    checkUrlParams();
  }, [location, fetchThread]); // Re-run this effect when location changes

  return (
    <div className="discussions-container">
      {view === 'list' && (
        <>
          <div className="discussions-header">
            <h2 className="section-title">Discussions</h2>
            <button 
              className="create-thread-btn"
              onClick={createNewThread}
            >
              Start a New Thread
            </button>
          </div>
          
          <div className="categories-filter">
            {categories.map(cat => (
              <button
                key={cat.id}
                className={`category-btn ${category === cat.id ? 'active' : ''}`}
                onClick={() => changeCategory(cat.id)}
              >
                {cat.name}
              </button>
            ))}
          </div>
          
          <ThreadList 
            threads={threads}
            loading={loading}
            error={error}
            hasMore={hasMore}
            onLoadMore={loadMore}
            onThreadClick={viewThread}
          />
        </>
      )}
      
      {view === 'detail' && selectedThreadId && (
        <ThreadDetail 
          threadId={selectedThreadId}
          thread={thread}
          comments={comments}
          loading={loading}
          error={error}
          onBack={backToList}
        />
      )}
      
      {view === 'create' && (
        <CreateThread 
          onCancel={backToList}
          onSuccess={handleThreadCreated}
        />
      )}
    </div>
  );
};

export default Discussions;